import React, { useState, useEffect } from 'react';
import { Plus, Search, Mail, X, Archive, Star, Edit3, SlidersHorizontal } from 'lucide-react';
import { db } from '../../../firebase';
import { collection, addDoc, getDocs, updateDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { notify } from '../../../utils/notify';

/** The project form's fields, shared by Add and Edit so they cannot drift apart */
const ProjectFields = ({ formData, setFormData, options }) => {
  const { sites, helpNeededOptions, projectTypes, statusOptions } = options;
  return (
    <>
              <div>
                <label className="eyebrow mb-1.5 block">
                  Project Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.projectTitle}
                  onChange={(e) => setFormData({ ...formData, projectTitle: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                  maxLength={200}
                  required
                />
              </div>

              <div>
                <label className="eyebrow mb-1.5 block">
                  Principal Investigator (PI) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.facultyPI}
                  onChange={(e) => setFormData({ ...formData, facultyPI: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                  required
                />
              </div>

              <div>
                <label className="eyebrow mb-1.5 block">
                  Site / Hospital <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.site}
                  onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                  required
                >
                  <option value="">Select a site</option>
                  {sites.map((site) => (
                    <option key={site} value={site}>{site}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="eyebrow mb-1.5 block">
                  PI Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                  required
                />
              </div>

              <div>
                <label className="eyebrow mb-1.5 block">
                  Additional Help Needed
                </label>
                <select
                  value={formData.helpNeeded}
                  onChange={(e) => setFormData({ ...formData, helpNeeded: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                >
                  {helpNeededOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="eyebrow mb-1.5 block">
                  Project Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.projectType}
                  onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                  required
                >
                  <option value="">Select project type</option>
                  {projectTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="eyebrow mb-1.5 block">
                  Current Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.currentStatus}
                  onChange={(e) => setFormData({ ...formData, currentStatus: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                  required
                >
                  <option value="">Select status</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
    </>
  );
};

const ActiveProjectsTab = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterHelpNeeded, setFilterHelpNeeded] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');

  // Form state
  const [formData, setFormData] = useState({
    projectTitle: '',
    facultyPI: '',
    site: '',
    email: '',
    helpNeeded: 'None',
    projectType: '',
    currentStatus: ''
  });

  const sites = [
    'Ben Taub',
    'Baylor St. Luke\'s',
    'Texas Heart Institute',
    'Texas Children\'s',
    'VA Medical Center',
    'MD Anderson',
    'Multiple Sites',
    'Other'
  ];

  const helpNeededOptions = [
    'None',
    '1 student, resident, or fellow',
    '1-2 students, residents, or fellows',
    '3-4 students, residents, or fellows',
    'Open to all interested'
  ];

  const projectTypes = [
    'Quality Improvement',
    'Case Report',
    'Retrospective Study',
    'Prospective Study',
    'Systematic Review/Meta-Analysis',
    'Basic Science',
    'Other'
  ];

  const statusOptions = [
    'Planning / Ideation',
    'Protocol Development',
    'Drafting IRB Submission',
    'IRB Submitted - Pending Approval',
    'IRB Approved - Planning',
    'Active Data Collection',
    'Data Analysis',
    'Manuscript Preparation',
    'Submitted for Publication',
    'Under Review',
    'Accepted - In Press',
    'Published',
    'On Hold',
    'Completed'
  ];

  // Site abbreviation mapping for search
  const siteAbbreviations = {
    'thi': 'Texas Heart Institute',
    'bsl': 'Baylor St. Luke\'s',
    'tch': 'Texas Children\'s',
    'va': 'VA Medical Center',
    'mda': 'MD Anderson',
    'bt': 'Ben Taub'
  };

  const getStatusColor = (status) => {
    const purpleStatuses = ['Planning / Ideation', 'Protocol Development'];
    const yellowStatuses = ['Drafting IRB Submission', 'IRB Submitted - Pending Approval'];
    const orangeStatuses = ['IRB Approved - Planning'];
    const blueStatuses = ['Active Data Collection', 'Data Analysis'];
    const greenStatuses = ['Manuscript Preparation', 'Submitted for Publication', 'Under Review', 'Accepted - In Press'];
    const tealStatuses = ['Published'];
    const grayStatuses = ['On Hold', 'Completed'];

    if (purpleStatuses.includes(status)) return 'bg-purple-100 text-purple-800 border-purple-300';
    if (yellowStatuses.includes(status)) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (orangeStatuses.includes(status)) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (blueStatuses.includes(status)) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (greenStatuses.includes(status)) return 'bg-green-100 text-green-800 border-green-300';
    if (tealStatuses.includes(status)) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    if (grayStatuses.includes(status)) return 'bg-gray-100 text-gray-800 border-gray-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    filterAndSortProjects();
  }, [projects, searchQuery, showArchived, filterStatus, filterSite, filterHelpNeeded, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll when any modal is open
  useEffect(() => {
    if (showModal || showEditModal || showFiltersModal) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [showModal, showEditModal, showFiltersModal]);

  const loadProjects = async () => {
    try {
      const q = query(collection(db, 'research_projects'), orderBy('submittedDate', 'desc'));
      const querySnapshot = await getDocs(q);
      const projectsData = [];
      querySnapshot.forEach((doc) => {
        projectsData.push({ id: doc.id, ...doc.data() });
      });
      setProjects(projectsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading projects:', error);
      setLoading(false);
    }
  };

  const filterAndSortProjects = () => {
    let filtered = projects.filter(project =>
      showArchived ? project.archived : !project.archived
    );

    // Apply filters
    if (filterStatus) {
      filtered = filtered.filter(project => project.currentStatus === filterStatus);
    }
    if (filterSite) {
      filtered = filtered.filter(project => project.site === filterSite);
    }
    if (filterHelpNeeded) {
      filtered = filtered.filter(project => project.helpNeeded === filterHelpNeeded);
    }

    // Apply search with abbreviation support
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      // Check if query matches an abbreviation
      const expandedQuery = siteAbbreviations[lowerQuery] || searchQuery;

      filtered = filtered.filter(project =>
        project.projectTitle?.toLowerCase().includes(lowerQuery) ||
        project.facultyPI?.toLowerCase().includes(lowerQuery) ||
        project.site?.toLowerCase().includes(lowerQuery) ||
        project.site?.toLowerCase().includes(expandedQuery.toLowerCase()) ||
        project.projectType?.toLowerCase().includes(lowerQuery)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return (b.submittedDate?.seconds || 0) - (a.submittedDate?.seconds || 0);
        case 'date-asc':
          return (a.submittedDate?.seconds || 0) - (b.submittedDate?.seconds || 0);
        case 'title-asc':
          return (a.projectTitle || '').localeCompare(b.projectTitle || '');
        case 'title-desc':
          return (b.projectTitle || '').localeCompare(a.projectTitle || '');
        case 'pi-asc':
          return (a.facultyPI || '').localeCompare(b.facultyPI || '');
        case 'pi-desc':
          return (b.facultyPI || '').localeCompare(a.facultyPI || '');
        case 'status':
          return statusOptions.indexOf(a.currentStatus) - statusOptions.indexOf(b.currentStatus);
        default:
          return 0;
      }
    });

    setFilteredProjects(sorted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.projectTitle || !formData.facultyPI || !formData.site || !formData.email || !formData.projectType || !formData.currentStatus) {
      notify.warn('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      notify.warn('Please enter a valid email address');
      return;
    }

    try {
      await addDoc(collection(db, 'research_projects'), {
        ...formData,
        submittedDate: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        archived: false,
        featured: false
      });
      notify.success('Your project has been added successfully. Thank you!');
      setShowModal(false);
      resetForm();
      loadProjects();
    } catch (error) {
      console.error('Error adding project:', error);
      notify.error('Error adding project. Please try again.');
    }
  };

  const handleArchive = async (projectId, projectTitle) => {
    if (window.confirm('Are you sure you want to archive this project? The Project PI will be notified of the change by email.')) {
      try {
        await updateDoc(doc(db, 'research_projects', projectId), {
          archived: true,
          lastUpdated: serverTimestamp()
        });

        // TODO: Send email notification to faculty
        // This will be implemented when email service is set up
        notify.success('Project archived successfully.');
        loadProjects();
      } catch (error) {
        console.error('Error archiving project:', error);
        notify.error('Error archiving project. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      projectTitle: '',
      facultyPI: '',
      site: '',
      email: '',
      helpNeeded: 'None',
      projectType: '',
      currentStatus: ''
    });
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      projectTitle: project.projectTitle,
      facultyPI: project.facultyPI,
      site: project.site,
      email: project.email,
      helpNeeded: project.helpNeeded,
      projectType: project.projectType,
      currentStatus: project.currentStatus
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingProject(null);
    resetForm();
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.projectTitle || !formData.facultyPI || !formData.site || !formData.email || !formData.projectType || !formData.currentStatus) {
      notify.warn('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      notify.warn('Please enter a valid email address');
      return;
    }

    try {
      await updateDoc(doc(db, 'research_projects', editingProject.id), {
        ...formData,
        lastUpdated: serverTimestamp()
      });
      notify.success('Project updated successfully!');
      setShowEditModal(false);
      setEditingProject(null);
      resetForm();
      loadProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      notify.error('Error updating project. Please try again.');
    }
  };

  const featuredProjects = filteredProjects.filter(p => p.featured && !p.archived);

  return (
    <div className="space-y-4">
      <button onClick={() => setShowModal(true)} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-semibold bg-emerald-700 hover:bg-emerald-800 transition-colors">
        <Plus size={18} /> Add a project
      </button>
      {/* Featured Projects Carousel */}
      {featuredProjects.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="eyebrow">Featured</h3>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {featuredProjects.map((project) => (
              <div key={project.id} className="flex-shrink-0 w-72 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card p-4 border-t-4 border-t-emerald-700">
                <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400"><Star size={12} className="fill-current" /> Featured</div>
                <h4 className="font-semibold text-[15px] text-gray-900 dark:text-white leading-tight mb-1">{project.projectTitle}</h4>
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5 mb-3">
                  <div>{project.facultyPI} · {project.site}</div>
                  {project.helpNeeded !== 'None' && <div className="font-medium text-emerald-700 dark:text-emerald-400">{/^open/i.test(project.helpNeeded) ? project.helpNeeded : `Seeking ${project.helpNeeded}`}</div>}
                </div>
                <a href={`mailto:${project.email}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white bg-emerald-700 hover:bg-emerald-800 transition-colors"><Mail size={15} /> Email the PI</a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter and Sort Button */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setShowFiltersModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-card hover:border-emerald-500 dark:hover:border-emerald-400 hover:shadow-card-hover transition-all"
        >
          <SlidersHorizontal size={18} className="text-gray-600 dark:text-gray-400" />
          <span className="font-medium text-gray-700 dark:text-gray-300">
            Filters {(filterStatus || filterSite || filterHelpNeeded) &&
              `(${[filterStatus, filterSite, filterHelpNeeded].filter(Boolean).length})`}
          </span>
        </button>

        {/* Sort By - Keep visible */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-card focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 hover:border-emerald-500 dark:hover:border-emerald-400 transition-all font-medium text-gray-700 dark:text-gray-300"
        >
          <option value="date-desc">Newest First</option>
          <option value="date-asc">Oldest First</option>
          <option value="title-asc">Title (A-Z)</option>
          <option value="title-desc">Title (Z-A)</option>
          <option value="pi-asc">PI (A-Z)</option>
          <option value="pi-desc">PI (Z-A)</option>
          <option value="status">Status</option>
        </select>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card focus-within:border-emerald-500 dark:focus-within:border-emerald-400 hover:border-emerald-500 dark:hover:border-emerald-400 transition-all">
        <div className="relative">
          <Search className="absolute left-4 top-3.5 text-gray-400 dark:text-gray-500" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search projects, PI, site, or type"
            className="w-full pl-12 pr-4 py-3.5 rounded-xl focus:outline-none text-gray-800 dark:text-white bg-transparent placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>
      </div>

      {/* Projects List */}
      <div>
        <h3 className="eyebrow mb-2 px-1">
          {showArchived ? 'Archived' : 'Active'} · {filteredProjects.length}
        </h3>

        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading projects...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center shadow-card">
            <Search size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery ? `No projects found matching "${searchQuery}"` : 'No projects available'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProjects.map((project) => (
              <div key={project.id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-card hover:border-emerald-500 dark:hover:border-emerald-400 hover:shadow-card-hover transition-all">
                <div className="flex items-start justify-between mb-3">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.currentStatus)}`}>
                    {project.currentStatus}
                  </span>
                  {!project.archived && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(project)}
                        className="text-gray-400 dark:text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        title="Edit project"
                      >
                        <Edit3 size={18} />
                      </button>
                      <button
                        onClick={() => handleArchive(project.id, project.projectTitle)}
                        className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Archive project"
                      >
                        <Archive size={18} />
                      </button>
                    </div>
                  )}
                </div>

                <h4 className="font-semibold text-[15px] text-gray-900 dark:text-white leading-tight mb-1">{project.projectTitle}</h4>
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5 mb-3">
                  <div>{project.facultyPI} · {project.site}</div>
                  {project.helpNeeded !== 'None' && <div className="font-medium text-emerald-700 dark:text-emerald-400">{/^open/i.test(project.helpNeeded) ? project.helpNeeded : `Seeking ${project.helpNeeded}`}</div>}
                </div>

                <div className="flex items-center justify-between">
                  <a
                    href={`mailto:${project.email}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white bg-emerald-700 hover:bg-emerald-800 transition-colors"><Mail size={15} /> Email the PI</a>
                  <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                    {project.projectType}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Show Archived Toggle */}
      <button
        onClick={() => setShowArchived(!showArchived)}
        className="w-full text-sm font-medium text-gray-500 dark:text-gray-400 py-2 hover:text-emerald-700 dark:hover:text-emerald-400"
      >
        {showArchived ? 'Show active projects' : 'Show archived projects'}
      </button>

      {/* Add Project Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn">
          <div role="dialog" className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-white">Add a project</h3>
              <button onClick={() => setShowModal(false)} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 pt-2 pb-4 space-y-4">
              <ProjectFields formData={formData} setFormData={setFormData} options={{ sites, helpNeededOptions, projectTypes, statusOptions }} />

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-xl text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-emerald-700 text-white rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-colors"
                >Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn">
          <div role="dialog" className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-white">Filter</h3>
              <button onClick={() => setShowFiltersModal(false)} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={20} />
              </button>
            </div>

            <div className="px-5 pt-2 pb-4 space-y-4">
              {/* Filter By Status */}
              <div>
                <label className="eyebrow mb-1.5 block">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Filter By Site */}
              <div>
                <label className="eyebrow mb-1.5 block">Site / Hospital</label>
                <select
                  value={filterSite}
                  onChange={(e) => setFilterSite(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                >
                  <option value="">All Sites</option>
                  {sites.map((site) => (
                    <option key={site} value={site}>{site}</option>
                  ))}
                </select>
              </div>

              {/* Filter By Help Needed */}
              <div>
                <label className="eyebrow mb-1.5 block">Help Needed</label>
                <select
                  value={filterHelpNeeded}
                  onChange={(e) => setFilterHelpNeeded(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:border-emerald-600"
                >
                  <option value="">All Projects</option>
                  {helpNeededOptions.filter(opt => opt !== 'None').map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilterStatus('');
                    setFilterSite('');
                    setFilterHelpNeeded('');
                  }}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-xl text-sm font-semibold"
                >Clear</button>
                <button
                  type="button"
                  onClick={() => setShowFiltersModal(false)}
                  className="flex-1 px-4 py-3 bg-emerald-700 text-white rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-colors"
                >Apply</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn">
          <div role="dialog" className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-white">Edit project</h3>
              <button onClick={handleCloseEditModal} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={20} />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="px-5 pt-2 pb-4 space-y-4">
              <ProjectFields formData={formData} setFormData={setFormData} options={{ sites, helpNeededOptions, projectTypes, statusOptions }} />

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-xl text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-emerald-700 text-white rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-colors"
                >Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ActiveProjectsTab;
